import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, UserLookupResponse, AdminItem } from '../services/api';
import {
  Shield,
  ArrowLeft,
  Search,
  UserPlus,
  Loader,
  AlertCircle,
  User,
  Trash2,
  Users,
} from 'lucide-react';

const AdminRoles: React.FC = () => {
  const navigate = useNavigate();
  const [codigo, setCodigo] = useState('');
  const [lookupResult, setLookupResult] = useState<UserLookupResponse | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [admins, setAdmins] = useState<AdminItem[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadAdmins = async () => {
    setAdminsLoading(true);
    setError('');
    try {
      const data = await adminAPI.getAdmins();
      setAdmins(data);
    } catch (err: any) {
      if (err.response?.status === 403) setError('Solo administradores pueden gestionar permisos.');
      else setError('Error al cargar la lista de administradores.');
    } finally {
      setAdminsLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, []);

  const handleBuscar = async () => {
    const cod = codigo.trim().toUpperCase();
    if (!cod) {
      setError('Ingresa un código de usuario.');
      setLookupResult(null);
      return;
    }
    setLookupLoading(true);
    setError('');
    setSuccess('');
    setLookupResult(null);
    try {
      const res = await adminAPI.lookupUser(cod);
      setLookupResult(res);
      if (!res.exists) setError('No existe un usuario con ese código en Citisalud.');
    } catch (err: any) {
      setLookupResult({ exists: false });
      setError(err.response?.data?.detail || 'Error al buscar el usuario.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleAgregarAdmin = async () => {
    if (!lookupResult?.exists || !lookupResult.cod_usuario) return;
    setAssignLoading(true);
    setError('');
    setSuccess('');
    try {
      await adminAPI.addAdmin(lookupResult.cod_usuario);
      setSuccess(`Se agregó a ${lookupResult.nombre || lookupResult.cod_usuario} como administrador.`);
      setLookupResult(null);
      setCodigo('');
      loadAdmins();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al agregar administrador.');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleQuitar = async (item: AdminItem) => {
    if (!window.confirm(`¿Quitar a ${item.nombre || item.cod_usuario} de la lista de administradores?`)) return;
    setError('');
    setSuccess('');
    try {
      await adminAPI.removeAdmin(item.cod_usuario);
      setSuccess('Administrador quitado correctamente.');
      loadAdmins();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al quitar.');
    }
  };

  return (
    <div className="animate-fade-in space-y-6 pb-4">
      <div className="max-w-4xl mx-auto">
        <div className="page-hero mb-2">
          <button type="button" onClick={() => navigate('/templates')} className="btn-secondary mb-4 py-2 text-sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a plantillas
          </button>
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-700 text-white shadow-md">
              <Shield className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
                Administración
              </p>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Administrar permisos</h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-600 sm:text-base">
                El acceso a la aplicación lo define Citisalud. Aquí solo se gestiona quién tiene rol de administrador en la base local.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="card bg-red-50 border-red-200 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}
        {success && (
          <div className="card bg-green-50 border-green-200 mb-6">
            <p className="text-green-800 font-medium">{success}</p>
          </div>
        )}

        <div className="card mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Agregar administrador</h2>
          <p className="text-sm text-gray-600 mb-4">
            Ingresa el código de usuario (ej: EMBL) y haz clic en Buscar. Se valida en la tabla de usuarios de Citisalud. Si existe, puedes agregarlo a la tabla de administradores.
          </p>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="label">Código de usuario</label>
              <input
                type="text"
                value={codigo}
                onChange={(e) => {
                  setCodigo(e.target.value);
                  setLookupResult(null);
                  setError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
                placeholder="Ej: EMBL"
                className="input-field"
                disabled={lookupLoading}
              />
            </div>
            <button
              type="button"
              onClick={handleBuscar}
              disabled={lookupLoading || !codigo.trim()}
              className="btn-primary flex items-center gap-2"
            >
              {lookupLoading ? <Loader className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              Buscar
            </button>
          </div>

          {lookupResult && (
            <div className="mt-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
              {lookupResult.exists ? (
                <>
                  <div className="flex items-center gap-2 text-gray-800">
                    <User className="w-5 h-5 text-gray-500" />
                    <span>
                      <strong>{lookupResult.cod_usuario}</strong>
                      {lookupResult.nombre && ` — ${lookupResult.nombre}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleAgregarAdmin}
                    disabled={assignLoading}
                    className="mt-3 btn-primary flex items-center gap-2"
                  >
                    {assignLoading ? <Loader className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />}
                    Agregar como administrador
                  </button>
                </>
              ) : (
                <p className="text-gray-600">No existe usuario con ese código en Citisalud.</p>
              )}
            </div>
          )}
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Administradores actuales
          </h2>
          {adminsLoading ? (
            <div className="flex justify-center py-8">
              <Loader className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : admins.length === 0 ? (
            <p className="text-gray-500 py-4">No hay administradores en la tabla.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Nombre</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {admins.map((item) => (
                    <tr key={item.cod_usuario} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.cod_usuario}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{item.nombre || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleQuitar(item)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100"
                        >
                          <Trash2 className="w-4 h-4" />
                          Quitar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminRoles;
